import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { TrendingUp, Users, AlertCircle, Bell, Search, User, Zap, Leaf, Crown, ArrowUpCircle, Menu, Sparkles} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import MetricCard from '../components/MetricCard';
import AIChatbot from '../pages/AIChatbot';
import DemoGuide from '../components/DemoGuide';
import DemoOnboarding from '../components/DemoOnboarding';
import UpgradeModal from '../components/UpgradeModal'; // New import

const Dashboard = ({ isDemoActive, setIsDemoActive }) => {
  const [companyData, setCompanyData] = useState({
    name: "Acme Corp",
    userName: "John Doe",
    esgScore: 76,
    carbonEmissionsChange: -5.2,
    diversityScore: 82,
    governanceRating: "A-",
    esgScores: [
      { name: 'Environmental', value: 80 },
      { name: 'Social', value: 75 },
      { name: 'Governance', value: 85 },
    ],
    carbonEmissionsTrend: [
      { month: 'Jan', emissions: 1200 },
      { month: 'Feb', emissions: 1150 },
      { month: 'Mar', emissions: 1100 },
      { month: 'Apr', emissions: 1050 },
      { month: 'May', emissions: 1000 },
      { month: 'Jun', emissions: 950 },
    ],
    diversityData: [
      { group: 'Management', diversity: 65 },
      { group: 'Staff', diversity: 78 },
      { group: 'New Hires', diversity: 82 },
    ],
    plan: 'Pro', // Current plan
  });

  const [aiPredictions, setAiPredictions] = useState({
    predictedESGScore: null,
    carbonReductionTarget: null,
    topESGRisk: null,
  });

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false); // New state

  useEffect(() => {
    // Simulate AI predictions
    const simulateAIPredictions = () => {
      const predictedESGScore = Math.min(100, companyData.esgScore + Math.random() * 5).toFixed(1);
      const latestEmission = companyData.carbonEmissionsTrend[companyData.carbonEmissionsTrend.length - 1].emissions;
      const carbonReductionTarget = (latestEmission * 0.9).toFixed(0);
      const risks = ['Climate Change Impact', 'Supply Chain Ethics', 'Data Privacy'];
      const topESGRisk = risks[Math.floor(Math.random() * risks.length)];

      setAiPredictions({ predictedESGScore, carbonReductionTarget, topESGRisk });
    };

    simulateAIPredictions();
  }, [companyData]);

  const handleOnboardingComplete = (formData) => {
    if (formData) {
      const newCompanyData = {
        ...companyData,
        name: formData.companyName || companyData.name,
        userName: formData.userName || companyData.userName,
        esgScore: calculateESGScore(formData),
        carbonEmissionsChange: formData.trackCarbonEmissions ? -5.2 : 0,
        diversityScore: parseInt(formData.womenPercentage) || companyData.diversityScore,
        governanceRating: (formData.hasBoard && formData.hasEthicsCode) ? "A-" : "B",
        esgScores: [
          { name: 'Environmental', value: calculateEnvironmentalScore(formData) },
          { name: 'Social', value: calculateSocialScore(formData) },
          { name: 'Governance', value: calculateGovernanceScore(formData) },
        ],
      };
      setCompanyData(newCompanyData);
    } else {
      console.error('Onboarding completed without form data');
    }
  };

  const calculateESGScore = (formData) => {
    const envScore = calculateEnvironmentalScore(formData);
    const socialScore = calculateSocialScore(formData);
    const govScore = calculateGovernanceScore(formData);
    return Math.round((envScore + socialScore + govScore) / 3);
  };

  const calculateEnvironmentalScore = (formData) => {
    return formData.trackCarbonEmissions ? 80 : 60;
  };

  const calculateSocialScore = (formData) => {
    return Math.min(100, Math.max(0, parseInt(formData.womenPercentage) || 0));
  };

  const calculateGovernanceScore = (formData) => {
    return (formData.hasBoard && formData.hasEthicsCode) ? 85 : 70;
  };

  const handleUpgrade = () => {
    setIsUpgradeModalOpen(true); // Open the upgrade modal
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header Area */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4">
          <Button 
            onClick={() => setIsDemoActive(true)}
            className="w-full sm:w-auto"
          >
            Start Guided Demo
          </Button>
          <DemoOnboarding onComplete={handleOnboardingComplete} />
        </div>
        <div className="flex items-center justify-between sm:justify-end space-x-2">
          <Badge 
            variant="secondary" 
            className="bg-primary/10 text-primary border border-primary/30 px-3 py-1 text-sm font-medium rounded-full flex items-center"
          >
            <Crown className="w-4 h-4 mr-1" /> {companyData.plan} Plan
          </Badge>
          <Button 
            onClick={handleUpgrade}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <ArrowUpCircle className="mr-2 h-4 w-4" />
            Upgrade
          </Button>
        </div>
      </div>
      
      {isDemoActive && <DemoGuide onClose={() => setIsDemoActive(false)} />}
      
      {/* User Info and Search */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center mb-8 dashboard-overview">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Welcome back, {companyData.userName}</h1>
          <p className="text-gray-600">{companyData.name} - ESG Dashboard</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Button variant="outline" size="icon" className="sm:hidden">
            <Menu className="h-4 w-4" />
          </Button>
          <div className="relative flex-grow sm:flex-grow-0">
            <Input
              type="text"
              placeholder="Search..."
              className="pl-8 w-full"
            />
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <Button variant="outline" size="icon">
            <Bell className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="hidden sm:inline-flex">
            <User className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* AI Predictions Section */}
      <Card className="mb-6 dashboard-ai-predictions">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Sparkles className="mr-2 h-5 w-5 text-purple-500" />
            AI-Powered Predictions and Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <h3 className="font-semibold mb-1">Predicted ESG Score (Next Quarter)</h3>
              <p className="text-2xl font-bold text-purple-600">{aiPredictions.predictedESGScore}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Carbon Reduction Target</h3>
              <p className="text-2xl font-bold text-green-600">{aiPredictions.carbonReductionTarget} tons</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Top ESG Risk to Address</h3>
              <p className="text-2xl font-bold text-orange-600">{aiPredictions.topESGRisk}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Summary */}
      <Card className="mb-6 dashboard-overview">
        <CardContent className="p-4">
          <p className="text-lg">
            Your ESG performance is <span className="font-bold text-green-600">improving</span>. 
            AI predicts your score will reach {aiPredictions.predictedESGScore} next quarter. 
            Focus on addressing {aiPredictions.topESGRisk} to maximize improvement.
          </p>
        </CardContent>
      </Card>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6 key-metrics">
        <MetricCard title="ESG Score" value={`${companyData.esgScore}/100`} trend={2.5} icon={TrendingUp} />
        <MetricCard title="Carbon Emissions" value={`${companyData.carbonEmissionsChange}%`} trend={-5.2} icon={TrendingUp} />
        <MetricCard title="Diversity Score" value={`${companyData.diversityScore}%`} trend={8} icon={Users} />
        <MetricCard title="Governance Rating" value={companyData.governanceRating} trend={1} icon={AlertCircle} />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <Card className="esg-performance-chart">
          <CardHeader>
            <CardTitle>ESG Performance Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={companyData.esgScores}>
                <PolarGrid />
                <PolarAngleAxis dataKey="name" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="ESG Score" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card className="carbon-emissions-chart">
          <CardHeader>
            <CardTitle>Carbon Emissions Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={companyData.carbonEmissionsTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="emissions" stroke="#8884d8" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="workforce-diversity-chart">
          <CardHeader>
            <CardTitle>Workforce Diversity</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={companyData.diversityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="group" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="diversity" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card className="ai-insights">
          <CardHeader>
            <CardTitle className="flex items-center">
            <Sparkles className="mr-2 h-5 w-5 text-yellow-500" />
              AI-Generated Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-4">
              <li className="flex items-start space-x-2">
                <TrendingUp className="h-5 w-5 mt-0.5 text-purple-500" />
                <span>AI projects your ESG score to reach {aiPredictions.predictedESGScore} next quarter, indicating positive momentum.</span>
              </li>
              <li className="flex items-start space-x-2">
                <Leaf className="h-5 w-5 mt-0.5 text-green-500" />
                <span>To align with industry leaders, aim to reduce carbon emissions to {aiPredictions.carbonReductionTarget} tons by next year.</span>
              </li>
              <li className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 mt-0.5 text-orange-500" />
                <span>Priority focus area: {aiPredictions.topESGRisk}. Developing strategies to address this could significantly boost your ESG performance.</span>
              </li>
              <li className="flex items-start space-x-2">
                <Users className="h-5 w-5 mt-0.5 text-blue-500" />
                <span>Employee diversity in management roles improved by 8% since new hiring initiatives.</span>
              </li>
              <li className="flex items-start space-x-2">
                <Zap className="h-5 w-5 mt-0.5 text-orange-500" />
                <span>Energy efficiency initiatives have reduced operational costs by 7.5%. Consider expanding to other facilities.</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* AI Chatbot */}
      <div className="mt-6 ai-chatbot">
        <AIChatbot />
      </div>

      {/* UpgradeModal */}
      <UpgradeModal 
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentPlan={companyData.plan}
      />
    </div>
  );
};

export default Dashboard;